import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Modal,
  ProgressBar
} from 'react-bootstrap';

/**
 * Automatic Job Submit component. This component automatically submits the job at the
 * start of the working step in the workflow
 * */
const AutoSubmitJob = ({
  step,
  setStep,
  onSubmit
}) => {
  useEffect(() => {
    if (step === 'working') {
      onSubmit()
        .then(() => setStep('completed'))
        .catch(() => setStep('error'));
    }
  }, [step]);

  return null;
};

/**
 * Modal Footer Component
 * */
const ModalFooter = ({
  onClose,
  downloadLink,
  step,
  setStep
}) => {
  const [disabled, setDisabled] = useState(false);
  let footer = null;

  switch (step) {
    case 'initial':
      footer = (
        <Modal.Footer>
          <Button variant='light' onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setDisabled(true);
              setStep('working');
            }}
            disabled={disabled}
          >
            Generate
          </Button>
        </Modal.Footer>
      );
      break;
    case 'working':
      footer = (
        <Modal.Footer>
          <Button variant='light' onClick={onClose}>
            Cancel
          </Button>
          <Button variant='primary' disabled>
            Generating...
          </Button>
        </Modal.Footer>
      );
      break;
    case 'completed':
      footer = (
        <Modal.Footer>
          <Button variant='light' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onClose}
            variant='primary'
            href={downloadLink}
            download
          >
            Download
          </Button>
        </Modal.Footer>
      );
      break;
    default: // Includes Error state
      footer = (
        <Modal.Footer>
          <Button variant='danger' onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      );
      break;
  }
  return footer;
};

const BulkUserJobModal = (props) => {
  const {
    className,
    initialTitle,
    workingTitle,
    completedTitle,
    errorTitle,
    jobId,
    autoSubmit,
    initialBody,
    workingBody,
    completedBody,
    errorBody,
    onClose,
    downloadLink,
    onSubmit
  } = props;
  const [step, setStep] = useState(autoSubmit ? 'working' : 'initial');

  const pageTitle = {
    initial: initialTitle,
    working: workingTitle,
    completed: completedTitle,
    error: errorTitle
  };

  const pageBody = {
    initial: initialBody,
    working: workingBody,
    completed: completedBody,
    error: errorBody
  };

  return (
    <Modal
      show
      className={className}
      onHide={onClose}
      animation={false}
      backdrop='static'
      keyboard={false}
    >
      <AutoSubmitJob
        step={step}
        setStep={setStep}
        onSubmit={onSubmit}
      />
      <Modal.Header>
        <h1>{pageTitle[step]}</h1>
        <button type='button' className='btn-close' aria-label='Close' onClick={onClose} />
      </Modal.Header>
      <Modal.Body>
        {pageBody[step]}
      </Modal.Body>
      <ModalFooter
        onClose={onClose}
        step={step}
        setStep={setStep}
        jobId={jobId}
        downloadLink={downloadLink}
      />
    </Modal>
  );
};

BulkUserJobModal.propTypes = {
  /** className prop */
  className: PropTypes.string,

  /** Title to display during each workflow step */
  initialTitle: PropTypes.string,
  workingTitle: PropTypes.string,
  completedTitle: PropTypes.string,
  errorTitle: PropTypes.string,

  /** Current Jobs Id */
  jobId: PropTypes.string,

  /** Should component skip initial step and autosubmit the job */
  autoSubmit: PropTypes.bool,

  /** Body of the modal for each workflow step */
  initialBody: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string
  ]),
  workingBody: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string
  ]),
  completedBody: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string
  ]),
  errorBody: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string
  ]),

  /** Download location of job files */
  downloadLink: PropTypes.string,

  /** onClose callback */
  onClose: PropTypes.func,

  /** onSubmit callback */
  onSubmit: PropTypes.func
};

BulkUserJobModal.defaultProps = {
  className: '',
  initialTitle: 'Select Job Options',
  workingTitle: 'Job In Progress',
  completedTitle: 'Download File',
  errorTitle: 'An Error Has Occured',
  jobId: '',
  autoSubmit: false,
  initialBody: null,
  workingBody: (
    <React.Fragment>
      <p>Please wait while we prepare your download. It may take a few minutes.</p>
      <p>Do not leave or close this window.</p>
      <ProgressBar striped animated variant='primary' now={100} />
    </React.Fragment>
  ),
  completedBody: <p>Your file is ready for download!</p>,
  errorBody: (
    <React.Fragment>
      <p>
        We&apos;re sorry, an error has occurred. You may try again.
        If the problem persists, please contact support.
      </p>
    </React.Fragment>
  ),
  downloadLink: '',
  onClose: () => false,
  onSubmit: () => false
};

export default BulkUserJobModal;
